/**
 * Browser-based locale and currency detection.
 * Maps navigator.language / timezone to currency, locale, and Amazon marketplace.
 */

export interface DetectedPreferences {
  currency: string;
  locale: string;
  marketplace: string;
  timezone: string;
}

/**
 * Language tag → currency/marketplace mapping.
 * Uses the region subtag (e.g. "en-US" → US) when available,
 * otherwise falls back to the primary language.
 */
const REGION_MAPPINGS: Record<string, { currency: string; marketplace: string; locale: string }> = {
  // Region subtags (most specific)
  US: { currency: "USD", marketplace: "US", locale: "en-US" },
  CA: { currency: "CAD", marketplace: "CA", locale: "en-CA" },
  AU: { currency: "AUD", marketplace: "AU", locale: "en-AU" },
  NZ: { currency: "AUD", marketplace: "AU", locale: "en-NZ" },
  GB: { currency: "GBP", marketplace: "UK", locale: "en-GB" },
  IE: { currency: "EUR", marketplace: "UK", locale: "en-IE" },
  DE: { currency: "EUR", marketplace: "DE", locale: "de-DE" },
  AT: { currency: "EUR", marketplace: "DE", locale: "de-AT" },
  CH: { currency: "EUR", marketplace: "DE", locale: "de-CH" },
  FR: { currency: "EUR", marketplace: "FR", locale: "fr-FR" },
  BE: { currency: "EUR", marketplace: "FR", locale: "fr-BE" },
  IT: { currency: "EUR", marketplace: "IT", locale: "it-IT" },
  ES: { currency: "EUR", marketplace: "ES", locale: "es-ES" },
  PT: { currency: "EUR", marketplace: "ES", locale: "pt-PT" },
  JP: { currency: "JPY", marketplace: "JP", locale: "ja-JP" },
  SG: { currency: "USD", marketplace: "US", locale: "en-SG" },
  HK: { currency: "USD", marketplace: "US", locale: "en-HK" },
};

/**
 * Timezone → region fallback for when navigator.language has no region subtag.
 */
const TIMEZONE_TO_REGION: Record<string, string> = {
  "Australia/Sydney": "AU",
  "Australia/Melbourne": "AU",
  "Australia/Brisbane": "AU",
  "Australia/Perth": "AU",
  "Australia/Adelaide": "AU",
  "Australia/Hobart": "AU",
  "Australia/Darwin": "AU",
  "Australia/Lord_Howe": "AU",
  "Pacific/Auckland": "NZ",
  "America/New_York": "US",
  "America/Chicago": "US",
  "America/Denver": "US",
  "America/Los_Angeles": "US",
  "America/Phoenix": "US",
  "America/Anchorage": "US",
  "Pacific/Honolulu": "US",
  "America/Toronto": "CA",
  "America/Vancouver": "CA",
  "America/Edmonton": "CA",
  "America/Winnipeg": "CA",
  "America/Halifax": "CA",
  "Europe/London": "GB",
  "Europe/Dublin": "IE",
  "Europe/Berlin": "DE",
  "Europe/Vienna": "AT",
  "Europe/Zurich": "CH",
  "Europe/Paris": "FR",
  "Europe/Brussels": "BE",
  "Europe/Rome": "IT",
  "Europe/Madrid": "ES",
  "Europe/Lisbon": "PT",
  "Asia/Tokyo": "JP",
  "Asia/Singapore": "SG",
  "Asia/Hong_Kong": "HK",
};

/**
 * Detect user preferences from browser APIs.
 * Uses navigator.language for region and Intl for timezone.
 */
export function detectBrowserPreferences(): DetectedPreferences {
  const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";
  const browserLang = navigator.language || "en-AU";

  // First try: timezone-based detection (strongest signal of physical location)
  const tzRegion = TIMEZONE_TO_REGION[timezone];
  if (tzRegion && REGION_MAPPINGS[tzRegion]) {
    const mapping = REGION_MAPPINGS[tzRegion];
    return { ...mapping, timezone };
  }

  // Second try: region from language tag (e.g. "en-US" → "US")
  const parts = browserLang.split("-");
  const region = parts.length >= 2 ? parts[parts.length - 1].toUpperCase() : null;
  if (region && REGION_MAPPINGS[region]) {
    const mapping = REGION_MAPPINGS[region];
    return { ...mapping, timezone };
  }

  // Fallback: AUD
  return {
    currency: "AUD",
    locale: "en-AU",
    marketplace: "AU",
    timezone,
  };
}

const DISMISSED_KEY = "prezntz_currency_detection_dismissed";

export function isCurrencyDetectionDismissed(): boolean {
  return localStorage.getItem(DISMISSED_KEY) === "true";
}

export function dismissCurrencyDetection(): void {
  localStorage.setItem(DISMISSED_KEY, "true");
}
